import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';
import getBaseURL from '@utils/api/getBaseURL';

import type { BundlePrebookResponseViewModel } from '../models/BundlePrebookResponseViewModel';
import type { BundlePrebookUpdateRequestViewModel } from '../models/BundlePrebookUpdateRequestViewModel';
import type { FlightPrebookAddBookOrderViewModel } from '../models/FlightPrebookAddBookOrderViewModel';
import type { FlightPrebookUpdateRequestViewModel } from '../models/FlightPrebookUpdateRequestViewModel';
import type { FlightPrebookViewModel } from '../models/FlightPrebookViewModel';
import type { HotelPrebookAddRequestViewModel } from '../models/HotelPrebookAddRequestViewModel';
import type { HotelPrebookUpdateRequestViewModel } from '../models/HotelPrebookUpdateRequestViewModel';
import type { HotelPrebookViewModel } from '../models/HotelPrebookViewModel';
import type { TourExtraServicePrebookAddModel } from '../models/TourExtraServicePrebookAddModel';
import type { TourExtraServicePrebookRequestViewModel } from '../models/TourExtraServicePrebookRequestViewModel';
import type { TourPrebookAddRequestViewModel } from '../models/TourPrebookAddRequestViewModel';
import type { TourPrebookUpdateRequestViewModel } from '../models/TourPrebookUpdateRequestViewModel';
import type { TourPrebookViewModel } from '../models/TourPrebookViewModel';
import type { TransferPrebookAddRequestViewModel } from '../models/TransferPrebookAddRequestViewModel';
import type { TransferPrebookAddViewModel } from '../models/TransferPrebookAddViewModel';
import type { TransferPrebookUpdateRequestViewModel } from '../models/TransferPrebookUpdateRequestViewModel';
import type { TransferPrebookUpdateViewModel } from '../models/TransferPrebookUpdateViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class PrebookService {
  /**
   * Hotel
   * Add prebook for hotel
   * @returns HotelPrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookAddHotel(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      /**
       * Prebook request model
       */
      body?: Array<HotelPrebookAddRequestViewModel>;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelPrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelPrebookViewModel>({
      method: 'POST',
      url: getBaseURL(`/v2/prebook/hotel`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotel
   * Update prebook for hotel
   * @returns HotelPrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookUpdateHotel(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Prebook item list request model
       */
      body?: HotelPrebookUpdateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelPrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelPrebookViewModel>({
      method: 'PUT',
      url: getBaseURL(`/v2/prebook/hotel`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flight
   * Add prebook for flight
   * @returns FlightPrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookAddFlight(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      body?: FlightPrebookAddBookOrderViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightPrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<FlightPrebookViewModel>({
      method: 'POST',
      url: getBaseURL(`/v2/prebook/flight`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flight
   * Update prebook for flight
   * @returns FlightPrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookUpdateFlight(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      body?: FlightPrebookUpdateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightPrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<FlightPrebookViewModel>({
      method: 'PUT',
      url: getBaseURL(`/v2/prebook/flight`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Tour
   * Add prebook for tour
   * @returns TourPrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookAddTour(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      /**
       * Prebook request model
       */
      body?: Array<TourPrebookAddRequestViewModel>;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourPrebookViewModel>({
      method: 'POST',
      url: getBaseURL(`/v2/prebook/tour`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Tour
   * Update prebook for tour
   * @returns TourPrebookViewModel Success
   * @throws ApiError
   */
  public static async prebookUpdateTour(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Prebook item list request model
       */
      body?: TourPrebookUpdateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourPrebookViewModel>({
      method: 'PUT',
      url: getBaseURL(`/v2/prebook/tour`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Extra Services (Tour)
   * Add prebook for tour extra service
   * @returns TourExtraServicePrebookAddModel Success
   * @throws ApiError
   */
  public static async prebookAddTourExtraService(
    {
      body,
    }: {
      body?: TourExtraServicePrebookRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourExtraServicePrebookAddModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourExtraServicePrebookAddModel>({
      method: 'POST',
      url: getBaseURL(`/v2/prebook/tour/extraservice`, `PrebookService`),
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Transfer
   * Add prebook for transfer
   * @returns TransferPrebookAddViewModel Success
   * @throws ApiError
   */
  public static async prebookAddTransfer(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      /**
       * Prebook item request model
       */
      body?: TransferPrebookAddRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TransferPrebookAddViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TransferPrebookAddViewModel>({
      method: 'POST',
      url: getBaseURL(`/v2/prebook/transfer`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Transfer
   * Update prebook for transfer
   * @returns TransferPrebookUpdateViewModel Success
   * @throws ApiError
   */
  public static async prebookUpdateTransfer(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Prebook item request model
       */
      body?: TransferPrebookUpdateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TransferPrebookUpdateViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TransferPrebookUpdateViewModel>({
      method: 'PUT',
      url: getBaseURL(`/v2/prebook/transfer`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Bundle
   * Update prebook for bundle
   * @returns BundlePrebookResponseViewModel Success
   * @throws ApiError
   */
  public static async prebookUpdateBundle(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Prebook item list request model
       */
      body?: BundlePrebookUpdateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<BundlePrebookResponseViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<BundlePrebookResponseViewModel>>({
      method: 'PUT',
      url: getBaseURL(`/v2/prebook/bundle`, `PrebookService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
